import React, { useEffect, useState } from 'react';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import AppBar from '@material-ui/core/AppBar';
import { useDispatch, useSelector } from 'react-redux';
import TableAnimationFiles from '../../../components/Animations/TableAnimationFiles.js';
import { getNotificationSettings, getPreferences, updatePreferences } from '../../../store/Notification/index';

const NotificationSettings = () => {
  const dispatch = useDispatch();
  const { categoryConfig, preferences, isSettingsLoading, isPreferencesLoading } = useSelector(state => state['Notification']);
  const [activeCategory, setActiveCategory] = useState(null);
  const [localPrefs, setLocalPrefs] = useState([]);

  useEffect(() => {
    dispatch(getNotificationSettings());
    dispatch(getPreferences());
  }, []);

  useEffect(() => {
    if (preferences && preferences.length > 0) {
      setLocalPrefs(JSON.parse(JSON.stringify(preferences)));
    }
  }, [preferences]);

  const handleToggle = (category, field, channel) => {
    const updated = localPrefs.map(p => {
      if (p.category === category) {
        const current = getChannels(p, field);
        return { ...p, [field]: { ...current, [channel]: !current[channel] } };
      }
      return p;
    });
    setLocalPrefs(updated);
    dispatch(updatePreferences({ preferences: updated }));
  };

  // Fall back to the first category until the user picks a tab (config loads async)
  const currentCategory = activeCategory || categoryConfig[0]?.category || false;
  const activeConfig = categoryConfig.find(c => c.category === currentCategory);
  const activePref = localPrefs.find(p => p.category === currentCategory);

  // Reason: only show the loader on first load — a background refetch shouldn't blank out already-rendered settings
  const isPageLoading = (isSettingsLoading || isPreferencesLoading) && !activeConfig;

  if (isPageLoading) return (
    <div className='notification-settings'>
      <div className='notification-settings-loader'>
        <TableAnimationFiles />
      </div>
    </div>
  );

  return (
    <div className='notification-settings'>
      <div className='tabs-box notification-tabs'>
        <div className='tabs-email'>
          <AppBar position='static'>
            <Tabs value={currentCategory} onChange={(_, category) => setActiveCategory(category)} aria-label='notification categories'>
              {categoryConfig.map(config => (
                <Tab key={config.category} value={config.category} label={config.label} />
              ))}
            </Tabs>
          </AppBar>
        </div>
      </div>
      <div className='notification-settings-header'>
        <h3>What you get notified about</h3>
        <p>Choose what topics matter to you and how you get notified about them.</p>
      </div>
      {activeConfig && (
        <div className='notification-tab-panel'>
          {activeConfig.alwaysOn || activeConfig.toggles.length === 0 ? (
            <div className='notification-always-on-note'>
              <span className='notification-always-on-badge'>Always on</span>
              <p>These notifications are always delivered and can't be turned off.</p>
            </div>
          ) : (
            <>
              <div className='notification-panel-header'>
                <span className='notification-panel-title'>{activeConfig.label}</span>
                <div className='notification-channel-headers'>
                  <span className='notification-channel-header'>In-App</span>
                  <span className='notification-channel-header'>Email</span>
                </div>
              </div>
              <div className='notification-category-items'>
                {activeConfig.toggles.map(toggle => {
                  const ch = getChannels(activePref, toggle.field);
                  return (
                    <div key={toggle.field} className='notification-toggle-item'>
                      <div className='notification-toggle-info'>
                        <span className='notification-toggle-label'>{toggle.label}</span>
                        {toggle.description && (
                          <span className='notification-toggle-desc'>{toggle.description}</span>
                        )}
                      </div>
                      <div className='notification-channel-checkboxes'>
                        <label className='notification-checkbox'>
                          <input
                            type='checkbox'
                            checked={ch.inApp}
                            onChange={() => handleToggle(activeConfig.category, toggle.field, 'inApp')}
                          />
                          <span className='notification-checkmark'></span>
                        </label>
                        <label className='notification-checkbox'>
                          <input
                            type='checkbox'
                            checked={ch.email}
                            onChange={() => handleToggle(activeConfig.category, toggle.field, 'email')}
                          />
                          <span className='notification-checkmark'></span>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Helper: read a preference value with backward compat (old boolean → { inApp, email })
const getChannels = (pref, field) => {
  if (!pref) return { inApp: true, email: false };
  const val = pref[field];
  if (val && typeof val === 'object') return { inApp: val.inApp !== false, email: val.email === true };
  if (typeof val === 'boolean') return { inApp: val, email: false };
  return { inApp: true, email: false };
};

export default NotificationSettings;
